import React, { useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Area, AreaChart, Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { ArrowUpRightIcon, BotIcon, CircleDollarSignIcon, ClipboardListIcon, LightbulbIcon, SparklesIcon, TrendingDownIcon, TrendingUpIcon } from "lucide-react";
import { BRAND } from "../lib/data";
import { TopBar } from "./TopBar";
type Department = "All" | "Luxury Concierge" | "Marketing Concierge" | "Listing Concierge" | "Digital Marketing";
const departments: Department[] = ["All", "Luxury Concierge", "Marketing Concierge", "Listing Concierge", "Digital Marketing"];
const revenueData = [{
  day: "Jul 1",
  actual: 10,
  projection: 12
}, {
  day: "Jul 5",
  actual: 28,
  projection: 27
}, {
  day: "Jul 10",
  actual: 44,
  projection: 42
}, {
  day: "Jul 15",
  actual: 65,
  projection: 58
}, {
  day: "Today",
  actual: 82,
  projection: 76
}, {
  day: "Jul 25",
  actual: null,
  projection: 101
}, {
  day: "Jul 31",
  actual: null,
  projection: 128
}];
const volumeData = [{
  name: "Luxury",
  volume: 18
}, {
  name: "Marketing",
  volume: 34
}, {
  name: "Listing",
  volume: 49
}, {
  name: "Digital",
  volume: 38
}];
const departmentMultipliers: Record<Department, {
  orders: string;
  total: string;
  projection: string;
  variance: string;
  direction: "up" | "down";
}> = {
  All: {
    orders: "139",
    total: "$82,460",
    projection: "$128,000",
    variance: "+$7,600",
    direction: "up"
  },
  "Luxury Concierge": {
    orders: "18",
    total: "$19,800",
    projection: "$29,400",
    variance: "+$2,100",
    direction: "up"
  },
  "Marketing Concierge": {
    orders: "34",
    total: "$21,740",
    projection: "$33,500",
    variance: "+$1,800",
    direction: "up"
  },
  "Listing Concierge": {
    orders: "49",
    total: "$25,900",
    projection: "$38,200",
    variance: "-$900",
    direction: "down"
  },
  "Digital Marketing": {
    orders: "38",
    total: "$15,020",
    projection: "$26,900",
    variance: "+$1,400",
    direction: "up"
  }
};
export function OperationsAnalytics({
  onHome,
  onBoard
}: {
  onHome: () => void;
  onBoard: () => void;
}) {
  const [department, setDepartment] = useState<Department>("All");
  const metrics = departmentMultipliers[department];
  const insights = useMemo(() => department === "All" ? [{
    title: "Zillow Showcase demand is climbing",
    body: "12% more agents ordered the Zillow Showcase this month than normal. Have you done anything different?",
    tone: "lime"
  }, {
    title: "Fastest approval loop this month",
    body: "Digital Marketing approvals are moving 18% faster than June — usually within 3.2 hours.",
    tone: "brand"
  }] : [{
    title: `${department} is outperforming`,
    body: "Order volume is trending ahead of the department's normal monthly pace. AI recommends reviewing staffing for the final week.",
    tone: "lime"
  }, {
    title: "Opportunity to unblock delivery",
    body: "A small group of orders is awaiting customer input. A targeted reminder could lift on-time delivery.",
    tone: "brand"
  }], [department]);
  return <div className="min-h-full w-full bg-neutral-50 text-neutral-900" data-id="element-368" data-mp="1ml1tekm31m90">
    <TopBar onHome={onHome} title="Operations Analytics" data-id="element-369" data-mp="1u3sugiunjg9s" />
    <main className="mx-auto max-w-[1400px] px-5 py-6 sm:px-8" data-id="element-370" data-mp="1z3xg0naf316v">
      <div className="flex flex-col gap-4 xl:flex-row xl:items-end xl:justify-between" data-id="element-371" data-mp="1z58huhkaxyvc">
        <div data-id="element-372" data-mp="1l5qnzns5fvf4"><p className="text-sm font-bold uppercase tracking-[0.14em] text-[#567500]" data-id="element-373" data-mp="1l5qpxtbpm5hr">Internal operations</p><h1 className="mt-1 font-heading text-3xl font-bold tracking-tight" data-id="element-374" data-mp="1p69hdm9ox9jf">Operations analytics</h1><p className="mt-1 text-[15px] text-neutral-500" data-id="element-375" data-mp="2cwn4qzhbyk7o">Live performance, delivery health, and AI-assisted opportunities.</p></div>
        <button onClick={onBoard} className="inline-flex items-center gap-2 self-start rounded-xl border border-neutral-300 bg-white px-4 py-2.5 text-sm font-semibold text-neutral-700 shadow-sm transition hover:bg-neutral-50" data-id="element-376" data-mp="2hw9g13dllqdu"><ClipboardListIcon className="h-4 w-4" data-id="element-377" data-mp="2vug5pw8e8k2f" /> View order routing board</button>
      </div>

      <div className="mt-6 flex max-w-full gap-2 overflow-x-auto rounded-xl border border-neutral-200 bg-white p-1.5 shadow-sm" data-id="element-378" data-mp="30kwjjhg4m7pb">
        {departments.map(item => <button key={item} onClick={() => setDepartment(item)} className={`whitespace-nowrap rounded-lg px-3 py-2 text-sm font-semibold transition ${department === item ? "text-white shadow-sm" : "text-neutral-500 hover:bg-neutral-100 hover:text-neutral-800"}`} style={department === item ? {
          backgroundColor: BRAND.accent
        } : undefined} data-id="element-379" data-mp="1cy61czxhpm5n">{item}</button>)}
      </div>

      <section className="mt-6 grid gap-4 sm:grid-cols-2 xl:grid-cols-4" data-id="element-380" data-mp="1vncca0s1l9gw">
        <MetricCard icon={<ClipboardListIcon data-id="element-382" data-mp="1qnhoi63kd002" />} label="Orders in progress" value={metrics.orders} note="Across active delivery stages" data-id="element-381" data-mp="1qnzuhug12sl6" />
        <MetricCard icon={<CircleDollarSignIcon data-id="element-384" data-mp="1lvq6wl2dy4m2" />} label="Month so far" value={metrics.total} note="Booked revenue to date" data-id="element-383" data-mp="1lw80f9xny4qa" />
        <MetricCard icon={<TrendingUpIcon data-id="element-386" data-mp="1htv4tecmrn78" />} label="Monthly projection" value={metrics.projection} note="Based on current pipeline" data-id="element-385" data-mp="1hvfpmc957rrw" />
        <MetricCard icon={metrics.direction === "up" ? <ArrowUpRightIcon data-id="element-388" data-mp="29m2d9rhzftaj" /> : <TrendingDownIcon data-id="element-389" data-mp="29kr75l12e9g9" />} label="Estimated over / under" value={metrics.variance} note="Versus monthly projection" positive={metrics.direction === "up"} data-id="element-387" data-mp="29n3y2nosvu5g" />
      </section>

      <section className="mt-6 grid gap-6 xl:grid-cols-[1.55fr_0.95fr]" data-id="element-390" data-mp="143sbhlpco0f">
        <div className="rounded-2xl border border-neutral-200 bg-white p-5 shadow-sm" data-id="element-391" data-mp="54mlbezc0047"><div className="flex items-start justify-between" data-id="element-392" data-mp="a3qu44icfvu0"><div data-id="element-393" data-mp="a5k2ppcioq4n"><h2 className="font-heading text-lg font-bold" data-id="element-394" data-mp="a5k0rjsyig20">Revenue pace</h2><p className="text-sm text-neutral-500" data-id="element-395" data-mp="f3lia0aqi7bd">Month so far vs. projected revenue ($k)</p></div><span className="rounded-full px-2.5 py-1 text-xs font-bold" style={{
              backgroundColor: BRAND.accentSoft,
              color: BRAND.accentText
            }} data-id="element-396" data-mp="k1wrq9x4aft2">Tracking ahead</span></div><div className="mt-5 h-72" data-id="element-397" data-mp="otol0s7nqzmw"><ResponsiveContainer width="100%" height="100%" data-id="element-398" data-mp="sugw7r03belt"><AreaChart data={revenueData} margin={{
                left: -20,
                right: 10,
                top: 8,
                bottom: 0
              }} data-id="element-399" data-mp="st6wkxdl9yd2"><defs data-id="element-400" data-mp="xucr90qt7vp8"><linearGradient id="revenue-fill" x1="0" y1="0" x2="0" y2="1" data-id="element-401" data-mp="xumi0qjoi6vz"><stop offset="5%" stopColor={BRAND.accent} stopOpacity={0.22} data-id="element-402" data-mp="12so0b99a626e" /><stop offset="95%" stopColor={BRAND.accent} stopOpacity={0} data-id="element-403" data-mp="12t62emimjam8" /></linearGradient></defs><CartesianGrid vertical={false} stroke="#ececec" data-id="element-404" data-mp="2ywkmjxi5yc5n" /><XAxis dataKey="day" tickLine={false} axisLine={false} tick={{
                  fill: "#a3a3a3",
                  fontSize: 12
                }} data-id="element-405" data-mp="2twpxlzhy7wn8" /><YAxis tickLine={false} axisLine={false} tick={{
                  fill: "#a3a3a3",
                  fontSize: 12
                }} tickFormatter={value => `$${value}k`} data-id="element-406" data-mp="38lm0li55ejgz" /><Tooltip contentStyle={{
                  borderRadius: 12,
                  border: "1px solid #e5e5e5"
                }} data-id="element-407" data-mp="33udqskzwow3a" /><Area type="monotone" dataKey="projection" stroke="#a3a3a3" strokeWidth={2} strokeDasharray="5 5" fill="none" data-id="element-408" data-mp="3hmagyji0kvx1" /><Area type="monotone" dataKey="actual" stroke={BRAND.accent} strokeWidth={3} fill="url(#revenue-fill)" data-id="element-409" data-mp="3dkyxywtnb8xt" /></AreaChart></ResponsiveContainer></div></div>
        <div className="rounded-2xl border border-neutral-200 bg-white p-5 shadow-sm" data-id="element-410" data-mp="3rjov2cggt8m6"><h2 className="font-heading text-lg font-bold" data-id="element-411" data-mp="3mmoqy736efxp">Order volume by department</h2><p className="text-sm text-neutral-500" data-id="element-412" data-mp="1x5nmgzv4578w">Month-to-date service requests</p><div className="mt-5 h-72" data-id="element-413" data-mp="1s7ce6tkgv4ss"><ResponsiveContainer width="100%" height="100%" data-id="element-414" data-mp="1s85mw02zk4dk"><BarChart data={volumeData} margin={{
                left: -22,
                right: 0,
                top: 8,
                bottom: 0
              }} data-id="element-415" data-mp="1s971ufn4hb0k"><CartesianGrid vertical={false} stroke="#ececec" data-id="element-416" data-mp="7qmrwx8rd9s4" /><XAxis dataKey="name" tickLine={false} axisLine={false} tick={{
                  fill: "#737373",
                  fontSize: 11
                }} data-id="element-417" data-mp="co50unmrzp04" /><YAxis tickLine={false} axisLine={false} tick={{
                  fill: "#a3a3a3",
                  fontSize: 12
                }} data-id="element-418" data-mp="3u4wf530ycbrb" /><Tooltip cursor={{
                  fill: "#f5f5f5"
                }} contentStyle={{
                  borderRadius: 12,
                  border: "1px solid #e5e5e5"
                }} data-id="element-419" data-mp="3u320g6g9xfvo" /><Bar dataKey="volume" fill={BRAND.accent} radius={[7, 7, 2, 2]} data-id="element-420" data-mp="2w8smjqz6ii6" /></BarChart></ResponsiveContainer></div></div>
      </section>

      <section className="mt-6" data-id="element-421" data-mp="3q3ktf8l78aoe"><div className="mb-3 flex items-center gap-2" data-id="element-422" data-mp="3q4vydbqdroh3"><span className="flex h-7 w-7 items-center justify-center rounded-lg bg-[#C6FF00] text-[#1A2E05]" data-id="element-423" data-mp="3b61yfohxh18i"><BotIcon className="h-4 w-4" data-id="element-424" data-mp="3g5549b5qfbhj" /></span><div data-id="element-425" data-mp="3g66fbfmqzxz9"><h2 className="font-heading text-lg font-bold" data-id="element-426" data-mp="3g66dda36tnwm">AI surface analytics</h2><p className="text-sm text-neutral-500" data-id="element-427" data-mp="19ex94z3gwcrc">What looks different — and where to act next.</p></div></div><div className="grid gap-4 lg:grid-cols-2" data-id="element-428" data-mp="1eea4k88ems5j">{insights.map(insight => <motion.article key={insight.title} initial={{
            opacity: 0,
            y: 7
          }} animate={{
            opacity: 1,
            y: 0
          }} className="flex items-start gap-4 rounded-2xl border border-neutral-200 bg-white p-5 shadow-sm" data-id="element-429" data-mp="1ebe68isna1gk"><span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${insight.tone === "lime" ? "bg-[#C6FF00] text-[#1A2E05]" : "text-white"}`} style={insight.tone === "brand" ? {
              backgroundColor: BRAND.accent
            } : undefined} data-id="element-430" data-mp="3tcsuclfnxxge"><LightbulbIcon className="h-5 w-5" data-id="element-431" data-mp="3embjycl1vx7d" /></span><div className="min-w-0 flex-1" data-id="element-432" data-mp="3end6pebfi84x"><h3 className="font-semibold text-neutral-900" data-id="element-433" data-mp="3ejh1urpieoh4">{insight.title}</h3><p className="mt-1 text-sm leading-relaxed text-neutral-600" data-id="element-434" data-mp="3aincrv0bz3mn">{insight.body}</p><button className="mt-3 inline-flex items-center gap-1 text-sm font-bold" style={{
                color: BRAND.accentText
              }} data-id="element-435" data-mp="35m6lwt1qyb5v">Ask AI about this <SparklesIcon className="h-3.5 w-3.5" data-id="element-436" data-mp="30nveeoyxccqt" /></button></div></motion.article>)}</div></section>
    </main>
  </div>;
}
function MetricCard({
  icon,
  label,
  value,
  note,
  positive
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  note: string;
  positive?: boolean;
}) {
  return <article className="rounded-2xl border border-neutral-200 bg-white p-5 shadow-sm" data-id="element-437" data-mp="16df4tr2tsdc1"><span className={`flex h-9 w-9 items-center justify-center rounded-xl ${positive === undefined ? "text-white" : positive ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700"}`} style={positive === undefined ? {
      backgroundColor: BRAND.accent
    } : undefined} data-id="element-438" data-mp="1bchzqip3d36a">{icon}</span><p className="mt-4 text-sm font-medium text-neutral-500" data-id="element-439" data-mp="nfkmgmft4pgz">{label}</p><p className="mt-1 font-heading text-2xl font-bold tracking-tight text-neutral-900" data-id="element-440" data-mp="nhf9q7ejwto8">{value}</p><p className="mt-1 text-xs text-neutral-400" data-id="element-441" data-mp="sensoa7ix1qj">{note}</p></article>;
}